import { Injectable } from '@angular/core';

import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { ArrayHelper } from 'tiime-expert-utils';

import { CompanyApiService } from '@api-services/company-api.service';
import { ContractApiService } from '@gi/api/services/contract-api.service';
import { ProspectApiService } from '@gi/api/services/prospect-api.service';
import { Contract } from '@gi/models/contract';
import { Prospect } from '@gi/models/prospect';
import { Company } from '@models/company';

export interface SearchOverlayState {
  companiesLoading: boolean;
  companies: Company[];
  prospectsLoading: boolean;
  prospects: Prospect[];
  contractsLoading: boolean;
  contracts: Contract[];
}

export const initialState: SearchOverlayState = {
  companiesLoading: false,
  companies: [],
  prospectsLoading: false,
  prospects: [],
  contractsLoading: false,
  contracts: []
};

export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_DEBOUNCE = 300;

@Injectable()
export class SearchOverlayStore extends ComponentStore<SearchOverlayState> {
  readonly updateContract = this.updater((state, contract: Contract) => ({
    ...state,
    contracts: ArrayHelper.updateOne(state.contracts, contract)
  }));

  readonly updateProspect = this.updater((state, prospect: Prospect) => ({
    ...state,
    prospects: ArrayHelper.updateOne(state.prospects, prospect)
  }));

  readonly searchCompanies = this.effect((search$: Observable<string>) =>
    search$.pipe(
      tap(() => this.patchState({ companiesLoading: true })),
      debounceTime(SEARCH_DEBOUNCE),
      distinctUntilChanged(),
      switchMap((search: string) =>
        search.length >= SEARCH_MIN_LENGTH
          ? this.companyApiService.searchCompanies(search, null, true).pipe(catchError(() => of([])))
          : of([])
      ),
      tapResponse(
        (companies: Company[]) => this.patchState({ companiesLoading: false, companies }),
        () => this.patchState({ companiesLoading: false })
      )
    )
  );

  readonly searchProspects = this.effect((search$: Observable<string>) =>
    search$.pipe(
      tap(() => this.patchState({ prospectsLoading: true })),
      debounceTime(SEARCH_DEBOUNCE),
      distinctUntilChanged(),
      switchMap((search: string) =>
        search.length >= SEARCH_MIN_LENGTH
          ? this.prospectApiService.searchProspects(search).pipe(catchError(() => of([])))
          : of([])
      ),
      tapResponse(
        (prospects: Prospect[]) => this.patchState({ prospectsLoading: false, prospects }),
        () => this.patchState({ prospectsLoading: false })
      )
    )
  );

  readonly searchContracts = this.effect((search$: Observable<string>) =>
    search$.pipe(
      tap(() => this.patchState({ contractsLoading: true })),
      debounceTime(SEARCH_DEBOUNCE),
      distinctUntilChanged(),
      switchMap((search: string) =>
        search.length >= SEARCH_MIN_LENGTH
          ? this.contractApiService.searchContracts(search).pipe(catchError(() => of([])))
          : of([])
      ),
      tapResponse(
        (contracts: Contract[]) => this.patchState({ contractsLoading: false, contracts }),
        () => this.patchState({ contractsLoading: false })
      )
    )
  );

  constructor(
    private readonly companyApiService: CompanyApiService,
    private readonly prospectApiService: ProspectApiService,
    private readonly contractApiService: ContractApiService
  ) {
    super(initialState);
  }
}
